import { Route } from '@angular/router';
import { UnauthorizedComponent } from './Pages/auth/unauthorized.component';
import { AdminAuthComponent } from './Pages/auth/auth.component';
import { ResetPasswordComponent } from './Pages/auth/reset-password.component';
import { CategoriesComponent } from './Pages/categories/categories.component';
import { HomeComponent } from './Pages/custom-home/home/home.component';
import { DashboardComponent } from './Pages/dashboard/dashboard.component';
import { InventoryListComponent } from './Pages/inventory/inventory-list/inventory-list.component';

import { LayoutComponent } from './Pages/layout/layout.component';
import { AllOrdersComponent } from './Pages/orders/all-orders/all-orders.component';
import { CompletedOrdersComponent } from './Pages/orders/completed-order/completed-orders.component';
import { PendingOrdersComponent } from './Pages/orders/pending-orders/pending-orders.component';
import { ReportPageComponent } from './Pages/reports/report-page/report-page.component';
import { AllRidersComponent } from './Pages/riders/rider-list/riders-list.component';
import { RiderVerificationListComponent } from './Pages/riders/rider-verification/rider-verification-list.component';
import { AddStoreComponent } from './Pages/store/add-store/add-store.component';
import { StoreListComponent } from './Pages/store/store-list/store-list.component';
import { Users } from './Pages/users/users.component';

import { AnalyticsDashboardComponent } from './Pages/analytics-dashboard/analytics-dashboard.component';
import { BrandsComponent } from './Pages/brands/brands.component';
import { CreateBrandComponent } from './Pages/brands/create-brand/create-brand.component';
import { AssignStoreProductsComponent } from './Pages/inventory/assign-store-product/assign-store-product.component';
import { StoreProductsComponent } from './Pages/inventory/store-product/store-product.component';
import { OrderDetailComponent } from './Pages/orders/order-detail/order-detail.component';
import { OrderLookupComponent } from './Pages/orders/order-lookup/order-lookup.component';
import { RefundRequestsComponent } from './Pages/orders/refund-requests/refund-requests.component';
import { RiderPayoutsComponent } from './Pages/riders/rider-payouts/rider-payouts.component';
import { RiderWalletAdminComponent } from './Pages/riders/rider-wallet-admin/rider-wallet-admin.component';
import { RedirectGuard } from './core/auth/RedirectGuard.guard';
import { AuthGuard } from './core/auth/auth.guard';

import { ADMIN_PANEL_ROLES, STORE_OPERATIONS_ROLES, FINANCE_ROLES, PICKING_ROLES, COUNTING_ROLES } from './core/auth/roles';
import { AdminHomeComponent } from './Pages/auth/admin-home.component';

const SUPERADMIN = ['superadmin'];
const STORE_OPERATIONS = [...STORE_OPERATIONS_ROLES];

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    component: AdminHomeComponent,
  },
  {
    path: 'login',
    component: AdminAuthComponent,
    canActivate: [RedirectGuard],
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },

  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    data: { roles: [...ADMIN_PANEL_ROLES] },
    // Additive operational routes preserve existing order and finance entry points.
    children: [
      { path: 'operations/picking', loadComponent: () => import('./Pages/operations/picking-workspace.component').then(m => m.PickingWorkspaceComponent), data: { roles: [...PICKING_ROLES] } },
      { path: 'inventory/receiving', loadComponent: () => import('./Pages/operations/receiving.component').then(m => m.ReceivingComponent), data: { roles: [...COUNTING_ROLES] } },
      { path: 'settings/account-details', loadComponent: () => import('./Pages/settings/account-details/account-details.component').then(m => m.AccountDetailsComponent), data: { roles: [...ADMIN_PANEL_ROLES] } },
      { path: 'operations/needs-action', loadComponent: () => import('./Pages/operations/needs-action.component').then(m => m.NeedsActionComponent), data: { roles: STORE_OPERATIONS } },
      { path: 'finance/reconciliation', loadComponent: () => import('./Pages/operations/reconciliation.component').then(m => m.ReconciliationComponent), data: { roles: [...FINANCE_ROLES] } },
      { path: 'inventory/stock-count', loadComponent: () => import('./Pages/operations/stock-count.component').then(m => m.StockCountComponent), data: { roles: [...COUNTING_ROLES] } },
      { path: 'operations/support', loadComponent: () => import('./Pages/operations/support-cases.component').then(m => m.SupportCasesComponent), data: { roles: STORE_OPERATIONS } },
      { path: 'operations/audit', loadComponent: () => import('./Pages/operations/sensitive-audit.component').then(m => m.SensitiveAuditComponent), data: { roles: SUPERADMIN } },
      { path: 'operations/access', loadComponent: () => import('./Pages/operations/privileged-access.component').then(m => m.PrivilegedAccessComponent), data: { roles: [...FINANCE_ROLES] } },
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'users',
        component: Users,
        data: { roles: SUPERADMIN },
      },
      // Legacy path
      { path: 'customers', redirectTo: 'users', pathMatch: 'full' },
      { path: 'stores/add', component: AddStoreComponent, data: { roles: SUPERADMIN } },
      { path: 'categories', component: CategoriesComponent, data: { roles: SUPERADMIN } },
      { path: 'brands', component: BrandsComponent, data: { roles: SUPERADMIN } },
      { path: 'brands/create', component: CreateBrandComponent, data: { roles: SUPERADMIN } },
      { path: 'brands/edit/:id', component: CreateBrandComponent, data: { roles: SUPERADMIN } },
      { path: 'stores', component: StoreListComponent, data: { roles: SUPERADMIN } },
      { path: 'stores/add/:id', component: AddStoreComponent, data: { roles: SUPERADMIN } },
      { path: 'orders', component: AllOrdersComponent, data: { roles: STORE_OPERATIONS } },
      {
        path: 'orders/assisted-new',
        loadComponent: () => import('./Pages/orders/assisted-order/assisted-order.component').then(m => m.AssistedOrderComponent),
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'orders/pending',
        component: PendingOrdersComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'orders/completed',
        component: CompletedOrdersComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'orders/refunds',
        component: RefundRequestsComponent,
        data: { roles: [...FINANCE_ROLES] },
      },
      {
        path: 'orders/lookup',
        component: OrderLookupComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'orders/detail/:id',
        component: OrderDetailComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'inventory',
        data: { roles: STORE_OPERATIONS },
        children: [
          {
            path: 'buying-prices',
            data: { buyingPrices: true },
            loadComponent: () => import('./Pages/inventory/intelligence/inventory-intelligence.component').then(m => m.InventoryIntelligenceComponent),
          },
          {
            path: 'intelligence',
            loadComponent: () => import('./Pages/inventory/intelligence/inventory-intelligence.component').then(m => m.InventoryIntelligenceComponent),
          },
          {
            path: '',
            component: InventoryListComponent, // Store-wise inventory overview
          },
          {
            path: 'store-products',
            component: StoreProductsComponent, // List of products per store
          },
          {
            path: 'store-products/add',
            component: AssignStoreProductsComponent, // Assign products to store
          },
          {
            path: 'store-products/:storeId',
            component: StoreProductsComponent, // Inventory for specific store
          },
          // {
          //   path: 'low-stock',
          //   component: LowStockComponent,
          // },
        ],
      },
      // { path: 'inventory/low-stock', component: LowStockComponent },
      {
        path: 'products',
        data: { roles: SUPERADMIN },
        loadChildren: () =>
          import('./Pages/product/product.routes').then((m) => m.ProductRoutes),
      },

      // { path: 'products/categories', component: CategoriesComponent },
      { path: 'riders', component: AllRidersComponent, data: { roles: STORE_OPERATIONS } },
      {
        path: 'riders/verification',
        component: RiderVerificationListComponent,
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'riders/payouts',
        component: RiderPayoutsComponent,
        data: { roles: [...FINANCE_ROLES] },
      },
      {
        path: 'riders/wallets',
        component: RiderWalletAdminComponent,
        data: { roles: [...FINANCE_ROLES] },
      },
      {
        path: 'riders/payout-rules',
        data: { roles: SUPERADMIN },
        loadComponent: () =>
          import(
            './Pages/rider-payout-rules/rider-payout-rules.component'
          ).then((m) => m.RiderPayoutRulesComponent),
      },
      { path: 'content/sections', component: HomeComponent, data: { roles: SUPERADMIN } },
      { 
        path: 'collections', 
        loadComponent: () => import('./Pages/collections/collections.component').then(m => m.CollectionsComponent),
        data: { roles: SUPERADMIN } 
      },
      { 
        path: 'collections/:id/sections', 
        loadComponent: () => import('./Pages/collections/collection-sections/collection-sections.component').then(m => m.CollectionSectionsComponent),
        data: { roles: SUPERADMIN } 
      },
      { 
        path: 'dynamic-tabs', 
        loadComponent: () => import('./Pages/dynamic-tabs/dynamic-tabs.component').then(m => m.DynamicTabsComponent),
        data: { roles: SUPERADMIN } 
      },
      { 
        path: 'content/themes', 
        loadComponent: () => import('./Pages/custom-home/themes/theme-management.component').then(m => m.ThemeManagementComponent),
        data: { roles: SUPERADMIN } 
      },
      {
        path: 'reports/orders',
        component: ReportPageComponent,
        data: { reportType: 'daily-orders', roles: ['superadmin'] },
      },
      {
        path: 'reports/revenue',
        component: ReportPageComponent,
        data: { reportType: 'daily-revenue', roles: ['superadmin'] },
      },
      {
        path: 'reports/products',
        component: ReportPageComponent,
        data: { reportType: 'top-products', roles: ['superadmin'] },
      },
      {
        path: 'reports/stores',
        component: ReportPageComponent,
        data: { reportType: 'store-performance', roles: ['superadmin'] },
      },
      {
        path: 'reports/riders',
        component: ReportPageComponent,
        data: { reportType: 'rider-performance', roles: ['superadmin'] },
      },
      {
        path: 'reports/customers',
        component: ReportPageComponent,
        data: { reportType: 'customer-analytics', roles: ['superadmin'] },
      },
      {
        path: 'reports/inventory',
        component: ReportPageComponent,
        data: { reportType: 'inventory', roles: ['superadmin'] },
      },
      {
        path: 'reports/coupons',
        component: ReportPageComponent,
        data: { reportType: 'coupon-usage', roles: ['superadmin'] },
      },
      {
        path: 'reports/payments',
        component: ReportPageComponent,
        data: { reportType: 'payment', roles: ['superadmin'] },
      },
      {
        path: 'reports/cancellations',
        component: ReportPageComponent,
        data: { reportType: 'cancellation', roles: ['superadmin'] },
      },
      {
        path: 'reports/search',
        loadComponent: () =>
          import('./Pages/search-analytics/search-analytics.component').then(
            (m) => m.SearchAnalyticsComponent,
          ),
        data: { roles: STORE_OPERATIONS },
      },
      {
        path: 'reports/sales',
        loadComponent: () =>
          import('./Pages/reports/sales-report/sales-report.component').then(
            (m) => m.SalesReportComponent,
          ),
        data: { roles: STORE_OPERATIONS },
      },
      { path: 'analytics', component: AnalyticsDashboardComponent, data: { roles: SUPERADMIN } },
      {
        path: 'stores/charges',
        data: { roles: SUPERADMIN },
        loadComponent: () =>
          import('./Pages/store-charges/store-charges.component').then(
            (m) => m.StoreChargesComponent
          ),
      },
      {
        path: 'stores/weather-management',
        data: { roles: STORE_OPERATIONS },
        loadComponent: () =>
          import(
            './Pages/store/weather-management/weather-management.component'
          ).then((m) => m.WeatherManagementComponent),
      },
      {
        path: 'stores/holidays',
        data: { roles: STORE_OPERATIONS },
        loadComponent: () =>
          import('./Pages/store/store-holidays/store-holidays.component').then(
            (m) => m.StoreHolidaysComponent
          ),
      },
      {
        path: 'app-management/rider-app',
        data: { roles: SUPERADMIN },
        loadComponent: () =>
          import('./Pages/rider-app/rider-app-releases.component').then(
            (m) => m.RiderAppReleasesComponent
          ),
      },
      {
        path: 'marketing/promotions',
        data: { roles: STORE_OPERATIONS },
        loadChildren: () =>
          import('./Pages/promotions/promotions.routes').then(
            (m) => m.PROMOTION_ROUTES
          ),
      },
      {
        path: 'settings',
        data: { roles: STORE_OPERATIONS },
        loadChildren: () =>
          import('./Pages/settings/settings.routes').then(
            (m) => m.SettingsRoutes
          ),
      },
    ],
  },

  // Wildcard
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: '**', redirectTo: '' },
];
